import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { oneKlicks } from '../../SDK/oneclick';
import { getSettings } from '../../services/settingsService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ImageShow from '../../Uitiles/ImageShow';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';


const InvoicePrint = ({ Id = "", genaretpdf = false, uploadserver = false }) => {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [error, setError] = useState(null);
  const [bankAccountDetails, setBankAccountDetails] = useState({});
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Fetch settings once
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        setSettings(data);
      } catch (err) {
        console.error("Failed to load settings:", err);
        setError("Failed to load settings");
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (genaretpdf && invoice && settings && Object.keys(settings).length > 0) {
      // Wait a bit for DOM to be fully rendered before generating PDF
      setTimeout(() => {
        handleGeneratePDF();
      }, 500);
    }
  }, [genaretpdf, invoice, settings]);

  // Fetch invoice whenever invoiceId or Id prop changes
  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      setError(null);
      try {
        const tableSchema = "";
        const tableName = "invoices";
        const orderBy = "id DESC";
        const search = {};
        const pagination = {};
        const margedata = [
          {
            "target_table": "users",
            "target_column": "id",
            "target_value": "client",
            "target_label": "users"
          },
          {
            "target_table": "users_billing_details",
            "target_column": "user_id",
            "target_value": "client",
            "target_label": "users_billing_details" //invoice_payments
          },
          {
            "target_table": "invoice_payments",
            "target_column": "invoice_id",
            "target_value": "id",
            "target_label": "invoice_payments" //invoice_payments
          },
          {
            "target_table": "virtual_accounts",
            "target_column": "user_id",
            "target_value": "client",
            "target_label": "accounts" //invoice_payments
          },
          {
            "target_table": "invoice_items",
            "target_column": "invoice_id",
            "target_value": "id",
            "target_label": "invoice_items",
            margedata: [
              {
                "target_table": "packages",
                "target_column": "id",
                "target_value": "packageId",
                "target_label": "packages"
              }
            ]

          }
        ];
        const conditions = {
          "id": Id || invoiceId
        };
        console.log("conditions", conditions);
        const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
        console.log("invoice response getInvoice :", response);
        if (response.success && response.data.length > 0) {
          setInvoice(response.data[0]);
          const receiversData = response.data?.[0]?.accounts?.[0]?.receivers || [];
          console.log("receiversData", receiversData);
          if (typeof receiversData === 'string') {
            const parsedReceivers = JSON.parse(receiversData);
            setBankAccountDetails(parsedReceivers?.[0] || {});
          } else {
            setBankAccountDetails(receiversData?.[0] || {});
          }
        } else {
          setInvoice(null);
          setError("Invoice not found");
        }
      } catch (err) {
        console.error("Failed to fetch invoice:", err);
        setInvoice(null);
        setError("Failed to fetch invoice");
      } finally {
        setLoading(false);
      }
    };

    if (Id || invoiceId) {
      fetchInvoice();
    } else {
      setLoading(false);
      setError("No invoice ID provided");
    }
  }, [Id, invoiceId, genaretpdf]);

  // Handle PDF generation with loading state
  const handleGeneratePDF = async () => {
    if (isGeneratingPDF) {
      console.log('PDF generation already in progress...');
      return;
    }
    
    setIsGeneratingPDF(true);
    try {
      const result = await generatePDF();
      if (uploadserver && result) {
        console.log('PDF uploaded successfully. URL:', result);
        alert(`PDF uploaded successfully!\nURL: ${result}`);
      }
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // HTML to PDF Generation Function with retry mechanism
  const generatePDF = async (retryCount = 0) => {
    const maxRetries = 3;
    
    if (!invoice) {
      console.error('PDF generation failed: No invoice data available');
      alert('Invoice data not available. Please refresh the page and try again.');
      return;
    }

    if (!settings || Object.keys(settings).length === 0) {
      console.error('PDF generation failed: Settings not loaded');
      alert('Settings not loaded. Please refresh the page and try again.');
      return;
    }

    try {
      // Get the invoice container element
      const invoiceElement = document.querySelector('.invoice-print-container');
      if (!invoiceElement) {
        if (retryCount < maxRetries) {
          console.warn(`Invoice element not found, retrying... (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => generatePDF(retryCount + 1), 1000);
          return;
        } else {
          alert('Invoice content not found. Please refresh the page and try again.');
          return;
        }
      }

      // Check if element has content
      if (invoiceElement.scrollHeight === 0 || invoiceElement.scrollWidth === 0) {
        if (retryCount < maxRetries) {
          console.warn(`Invoice element has no content, retrying... (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => generatePDF(retryCount + 1), 1000);
          return;
        } else {
          alert('Invoice content is empty. Please refresh the page and try again.');
          return;
        }
      }

      // Temporarily hide the action buttons for clean PDF
      const actionButtons = document.querySelector('.no-print');
      if (actionButtons) {
        actionButtons.style.display = 'none';
      }

      console.log('Starting PDF generation...', {
        elementSize: { width: invoiceElement.scrollWidth, height: invoiceElement.scrollHeight },
        retryAttempt: retryCount
      });

      // Generate canvas from HTML with timeout
      const canvas = await Promise.race([
        html2canvas(invoiceElement, {
          scale: 2, // Higher quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: invoiceElement.scrollWidth,
          height: invoiceElement.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false // Disable html2canvas logging
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('HTML2Canvas timeout')), 30000)
        )
      ]);

      // Show action buttons again
      if (actionButtons) {
        actionButtons.style.display = 'flex';
      }

      // Validate canvas
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Generated canvas is invalid');
      }

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Calculate dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Save the PDF
      const fileName = `Invoice_${invoice.id}_${(invoice.users_billing_details?.[0]?.name?.split(' ').join('_') || 'Client').replace(/\s+/g, '_')}.pdf`;
      
      if(uploadserver) {
        const root = 'invoice';
        const status = 'public';
        const file = pdf.output('blob');
        let fileurl = '';
        
        console.log('Uploading PDF to server...', { fileName, fileSize: file.size });
        
        const upload = await OneKlicksFileSDK.uploadFile(root, status, file);
        if(upload.success) {
          const uploadedFileName = upload.file.fileName;
          fileurl = upload.file.root + '/' + upload.file.fileName;
          console.log('PDF uploaded successfully:', { fileurl, uploadedFileName });
          const tableSchema = "";
          const tableName = "invoices";
          const conditions = {
            "id": invoice.id
          };
          const response = await oneKlicks.update(tableSchema, tableName, { file_url: fileurl }, conditions, false);
          console.log("response", response);
          if(response.success) {
            console.log('Invoice payment updated successfully:', { fileurl, uploadedFileName });
            return fileurl;
          } else {
            console.error('Invoice payment update failed:', response.error);
            throw new Error(`Update failed: ${response.error || 'Unknown error'}`);
          }
        } else {
          console.error('PDF upload failed:', upload.error);
          throw new Error(`Upload failed: ${upload.error || 'Unknown error'}`);
        }
      } else {
        pdf.save(fileName);
      }
      

      console.log('PDF generated successfully:', fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Show action buttons again in case of error
      const actionButtons = document.querySelector('.no-print');
      if (actionButtons) {
        actionButtons.style.display = 'flex';
      }

      if (retryCount < maxRetries && error.message.includes('timeout')) {
        console.warn(`PDF generation timeout, retrying... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => generatePDF(retryCount + 1), 2000);
      } else {
        const errorMessage = error.message.includes('timeout') 
          ? 'PDF generation is taking too long. Please try again.' 
          : 'Error generating PDF. Please refresh the page and try again.';
        alert(errorMessage);
      }
    }
  };

  // Render logic
  if (loading) {
    return <p>Loading invoice...</p>;
  }

  if (error) {
    return <p style={{ color: 'red' }}>{error}</p>;
  }


  return (
    <div className="invoice-print-container" style={{
      display: 'flex',
      flexDirection: 'column',
      width: '210mm',
      minHeight: '297mm',
      margin: '0 auto',
      padding: '10mm 10mm 10mm 10mm',
      backgroundColor: 'white',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      lineHeight: '1.4',
      color: '#333',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      paddingBottom: '15mm'
    }}>
      {invoice ? (
        <>
          {/* Print Styles */}
          <style>
            {`
              @media print {
                body * {
                  visibility: hidden;
                }
                .invoice-print-container, .invoice-print-container * {
                  visibility: visible;
                }
                .invoice-print-container {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100% !important;
                  height: 100% !important;
                  margin: 0 !important;
                  padding: 10mm 10mm 15mm 10mm !important;
                  box-shadow: none !important;
                }
                .no-print {
                  display: none !important;
                }
              }
              
              .invoice-header {
                border-bottom: 2px solid #2980b9;
                padding-bottom: 15px;
                margin-bottom: 10px;
              }
              
              .invoice-title {
                font-size: 15px;
                font-weight: bold;
                color: #ffffff;
                margin: 0;
              }
              
              .invoice-info {
                display: flex;
                justify-content: space-between;
                margin-bottom: 10px;
              }
              
              .company-details, .client-details {
                flex: 1;
              }
              
              .invoice-table {
                width: 100%;
                border-collapse: collapse;
                margin: 0 0;
              }
              
              .invoice-table th,
              .invoice-table td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              
              .invoice-table th {
                background-color: #2980b9;
                color: white;
                font-weight: bold;
              }
              
              .invoice-table .text-right {
                text-align: right;
              }
              
              .invoice-totals {
                margin-top: 20px;
                text-align: right;
              }
              
              .invoice-footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #ddd;
                text-align: center;
                font-size: 11px;
                color: #666;
              }
              
              .status-badge {
                display: inline-block;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 10px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                border: 2px solid transparent;
                position: relative;
                overflow: hidden;
              }
              
              .status-badge::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                transition: left 0.5s;
              }
              
              .status-badge:hover::before {
                left: 100%;
              }
              
              /* Paid Status - Green with success icon */
              .status-paid { 
                background: linear-gradient(135deg, #27ae60, #2ecc71);
                color: white;
                border-color: #27ae60;
                box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);
              }
              
              .status-paid::after {
                content: '✓';
                margin-left: 4px;
                font-weight: bold;
              }
              
              /* Pending Status - Orange with clock icon */
              .status-pending { 
                background: linear-gradient(135deg, #f39c12, #e67e22);
                color: white;
                border-color: #f39c12;
                box-shadow: 0 2px 8px rgba(243, 156, 18, 0.3);
              }
              
              .status-pending::after {
                content: '⏰';
                margin-left: 4px;
              }
              
              /* Overdue Status - Red with warning icon */
              .status-overdue { 
                background: linear-gradient(135deg, #e74c3c, #c0392b);
                color: white;
                border-color: #e74c3c;
                box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
                animation: pulse 2s infinite;
              }
              
              .status-overdue::after {
                content: '⚠';
                margin-left: 4px;
                font-weight: bold;
              }
              
              /* Draft Status - Gray with edit icon */
              .status-draft { 
                background: linear-gradient(135deg, #95a5a6, #7f8c8d);
                color: white;
                border-color: #95a5a6;
                box-shadow: 0 2px 8px rgba(149, 165, 166, 0.3);
              }

              .status-partial_payment { 
                background: linear-gradient(135deg, #3498db, #2980b9);
                color: white;
                border-color: #3498db;
                box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
              }

              .status-partial_payment::after {
                content: '💰';
                margin-left: 4px;
              }

              .status-draft::after {
                content: '✏';
                margin-left: 4px;
              }
              
              /* Partial Payment Status - Blue with info icon */
              .status-partial_pay { 
                background: linear-gradient(135deg, #3498db, #2980b9);
                color: white;
                border-color: #3498db;
                box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
              }
              
              .status-partial_pay::after {
                content: '💰';
                margin-left: 4px;
              }
              
              /* Cancelled Status - Dark red with X icon */
              .status-cancelled { 
                background: linear-gradient(135deg, #8e44ad, #9b59b6);
                color: white;
                border-color: #8e44ad;
                box-shadow: 0 2px 8px rgba(142, 68, 173, 0.3);
              }
              
              .status-cancelled::after {
                content: '✕';
                margin-left: 4px;
                font-weight: bold;
              }
              
              /* Default Status - Neutral gray */
              .status-default { 
                background: linear-gradient(135deg, #34495e, #2c3e50);
                color: white;
                border-color: #34495e;
                box-shadow: 0 2px 8px rgba(52, 73, 94, 0.3);
              }
              
              @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.05); }
                100% { transform: scale(1); }
              }
            `}
          </style>

          {/* Invoice Content */}
          <div className="invoice-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <img src={process.env.REACT_APP_API_BASE_LIVE_URL + settings.logo_url || ''} alt="Company Logo" style={{ height: '50px', marginLeft: '-8px' }} />
                <h1 className="invoice-title">{settings.site_name || 'Company Name'}</h1>
                <p style={{ margin: '5px 0', fontSize: '11px', color: '#ffffff' }}>
                  {settings.contact_address || 'Company Address'}
                </p>
                <p style={{ margin: '5px 0', fontSize: '11px', color: '#ffffff' }}>
                  Phone: {settings.contact_phone || 'N/A'} | Email: {settings.contact_email || 'N/A'}
                </p>
                <p style={{ margin: '5px 0', fontSize: '11px', color: '#ffffff' }}>
                  TAX ID: {settings.tax_id || 'N/A'} | GSTIN: {settings.gstin || 'N/A'}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#2980b9' }}>INVOICE</h2>
                <div style={{ fontSize: '11px', color: '#ffffff' }}>
                  <p style={{ margin: '2px 0', color: '#ffffff' }}><strong style={{ color: '#ffffff' }}>Invoice #:</strong> {invoice.id}</p>
                  <p style={{ margin: '2px 0', color: '#ffffff' }}><strong style={{ color: '#ffffff' }}>Date: </strong> {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : ''}</p>
                  <p style={{ margin: '2px 0', color: '#ffffff' }}><strong style={{ color: '#ffffff' }}>Due Date: </strong> {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : ''}</p>
                  <p style={{ margin: '10px 0 5px 0' }}>
                    <span className={`status-badge status-${(invoice.status?.toLowerCase() || 'draft').replace(/\s+/g, '_')}`}>
                      {invoice.status || 'Draft'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="invoice-info">
            <div className="company-details">
              <h4 style={{ margin: '0 0 10px 0', color: '#2980b9' }}>From:</h4>
              <p style={{ margin: '2px 0' }}><strong>{settings.site_name || 'Company Name'}</strong></p>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>{settings.contact_address || 'Company Address'}</p>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                Phone: {settings.contact_phone || 'N/A'} | Email: {settings.contact_email || 'N/A'}
              </p>
            </div>
            <div className="client-details">
              <h4 style={{ margin: '0 0 10px 0', color: '#2980b9' }}>Bill To:</h4>
              <p style={{ margin: '2px 0' }}><strong>{invoice.users_billing_details?.[0]?.name || 'Client Name'}</strong></p>
              <p style={{ margin: '2px 0' }}>{invoice.users_billing_details?.[0]?.address || 'Company Address'} {invoice.users_billing_details?.[0]?.city || 'City'} {invoice.users_billing_details?.[0]?.state || 'State'} {invoice.users_billing_details?.[0]?.country || 'Country'}</p>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>{invoice.users_billing_details?.[0]?.phone || 'Phone'} | {invoice.users_billing_details?.[0]?.email || 'client@email.com'}</p>
            </div>
          </div>

          <table className="invoice-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Description</th>
                <th>Package</th>
                <th>{invoice.tax_type_cgst === 'true' ? 'CGST %' : invoice.tax_type_sgst === 'true' ? 'SGST %' : invoice.tax_type_igst === 'true' ? 'IGST %' : invoice.tax_type_tax === 'true' ? 'Tax %' : '0'}</th>
                <th>TAX Amount</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.invoice_items && invoice.invoice_items.length > 0 ? (
                invoice.invoice_items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.packages[0]?.heading || 'Service Description'}</td>
                    <td>{item?.packages[0]?.name || 'Standard'}</td>
                    <td>{item.tax_rate_cgst || item.tax_rate_sgst || item.tax_rate_igst || item.tax_rate_tax || '0'}%</td>
                    <td>₹{(parseFloat(item.tax_amount_cgst) || parseFloat(item.tax_amount_sgst) || parseFloat(item.tax_amount_igst) || parseFloat(item.tax_amount_tax) || 0).toFixed(2)}</td>
                    <td className="text-right">₹{(parseFloat(item.price) || 0).toFixed(2)}</td>
                    <td className="text-right">₹{(parseFloat(item.total_amount) || 0).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>1</td>
                  <td>{invoice.description || 'Service Description'}</td>
                  <td>{invoice.package || 'Standard'}</td>
                  <td>1</td>
                  <td>0%</td>
                  <td>₹0.00</td>
                  <td className="text-right">₹{(parseFloat(invoice.amount) || 0).toFixed(2)}</td>
                  <td className="text-right">₹{(parseFloat(invoice.amount) || 0).toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Payment Summary - Compact Design */}
          {invoice.invoice_payments && invoice.invoice_payments.length > 0 && (
            <div style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px'
            }}>
              <h4 style={{
                margin: '0 0 10px 0',
                color: '#2980b9',
                fontSize: '13px',
                fontWeight: 'bold'
              }}>
                Payment History ({invoice.invoice_payments.length} payment{invoice.invoice_payments.length > 1 ? 's' : ''})
              </h4>
              <div style={{ display: 'grid', gap: '6px' }}>
                {invoice.invoice_payments.map((payment, index) => (
                  <div key={payment.id || index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 8px',
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '11px'
                  }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        fontWeight: 'bold'
                      }}>
                        {index + 1}
                      </span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#495057' }}>
                          {payment.transaction_id || 'N/A'}
                        </div>
                        <div style={{ color: '#6c757d', fontSize: '10px' }}>
                          {payment.payment_date} • {payment.payment_source}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 'bold', color: '#28a745', fontSize: '12px' }}>
                        ₹{parseFloat(payment.amount || 0).toFixed(2)}
                      </div>
                      {payment.reference_number && (
                        <div style={{ color: '#6c757d', fontSize: '9px' }}>
                          Ref: {payment.reference_number}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: '8px',
                padding: '6px 8px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #c3e6c3',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#155724',
                textAlign: 'center'
              }}>
                Total Paid: ₹{invoice.invoice_payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0).toFixed(2)}
              </div>
            </div>
          )}

          <div className='d-flex justify-content-between'>

            <div style={{ marginTop: '30px', fontSize: '11px' }}>
              <h4 style={{ margin: '0 0 0 0', color: '#2980b9' }}>Payment Terms:</h4>
              <p style={{ margin: '2px 0' }}>• Payment due within 30 days of invoice date</p>
              <p style={{ margin: '2px 0' }}>• Late payments may incur additional charges</p>
              <p style={{ margin: '2px 0' }}>• For questions about this invoice, contact {settings.contact_email || 'support@company.com'}</p>
              <h4 style={{ margin: '0 0 0 0', color: '#2980b9' }}>Payment Method:</h4>

              <div className='d-flex justify-content-between'>
                <p style={{ margin: '2px 0' }}>Bank Name:</p>
                <p style={{ margin: '2px 0' }}>{bankAccountDetails?.bank_name || settings.bank_name || 'N/A'}</p>
              </div>
              <div className='d-flex justify-content-between'>
                <p style={{ margin: '2px 0' }}>Account Number:</p>
                <p style={{ margin: '2px 0' }}>{bankAccountDetails?.account_number || settings.bank_account_number || 'N/A'}</p>
              </div>
              <div className='d-flex justify-content-between'>
                <p style={{ margin: '2px 0' }}>Account Holder:</p>
                <p style={{ margin: '2px 0' }}>{bankAccountDetails?.name || settings.bank_account_holder || 'N/A'}</p>
              </div>
              <div className='d-flex justify-content-between'>
                <p style={{ margin: '2px 0' }}>Account IFSC:</p>
                <p style={{ margin: '2px 0' }}>{bankAccountDetails?.ifsc || settings.bank_ifsc_code || 'N/A'}</p>
              </div>


            </div>

            <div className="invoice-totals " style={{ width: '40%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Subtotal:</span>
                <span>₹{(parseFloat(invoice.amount) || 0).toFixed(2)}</span>
              </div>
              {/* <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>{invoice.tax_type_cgst === 'true' ? 'CGST :' : invoice.tax_type_sgst === 'true' ? 'SGST :' : invoice.tax_type_igst === 'true' ? 'IGST :' : invoice.tax_type_tax === 'true' ? 'Tax :' : '0'}</span>
                <span>{(parseFloat(invoice.tax_rate_cgst) || parseFloat(invoice.tax_rate_sgst) || parseFloat(invoice.tax_rate_igst) || parseFloat(invoice.tax_rate_tax) || 0).toFixed(2)}%</span>
              </div> */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ width: '75px', textAlign: 'left' }}>{invoice.tax_type_cgst === 'true' ? 'CGST :' : invoice.tax_type_sgst === 'true' ? 'SGST :' : invoice.tax_type_igst === 'true' ? 'IGST :' : invoice.tax_type_tax === 'true' ? 'Tax :' : '0'}</span>
                <span>{(parseFloat(invoice.tax_rate_cgst) || parseFloat(invoice.tax_rate_sgst) || parseFloat(invoice.tax_rate_igst) || parseFloat(invoice.tax_rate_tax) || 0).toFixed(2)}%</span>
                <span>₹{(parseFloat(invoice.tax_amount_cgst) || parseFloat(invoice.tax_amount_sgst) || parseFloat(invoice.tax_amount_igst) || parseFloat(invoice.tax_amount_tax) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ width: '75px', textAlign: 'left' }}>Discount:</span>
                <span>{(parseFloat(invoice.discount) || 0).toFixed(2)}%</span>
                <span>₹{(parseFloat(invoice.discount_amount) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ width: '75px', textAlign: 'left' }} >Process Fee :</span>
                <span>{(parseFloat(invoice.processing_fee_percentage) || 2.5).toFixed(2)}%</span>
                <span>₹{(parseFloat(invoice.processing_fee) || 0).toFixed(2)}</span>
              </div>

              {invoice.paid_amount > 0 && (
                <>
                  <hr style={{ margin: '10px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>Paid Amount:</span>
                    <span>₹{(parseFloat(invoice.paid_amount) || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>Total Amount:</span>
                    <span>₹{(parseFloat(invoice.total_amount) || parseFloat(invoice.amount) || 0).toFixed(2)}</span>
                  </div>
                </>
              )}

              <hr style={{ margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold' }}>
                <span>{invoice.paid_amount > 0 ? 'Remaining Amount:' : 'Total Amount:'}</span>
                <span>₹{(parseFloat(invoice.total_amount - invoice.paid_amount) || parseFloat(invoice.amount) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>








          {/* Footer always at bottom */}
          <div className="" style={{
            flexShrink: 0,
            marginTop: 'auto',
            borderTop: '1px solid #ddd',
            paddingTop: '10px',
            textAlign: 'center',
            fontSize: '11px',
            color: '#666'
          }}>
            <div className='d-flex column justify-content-end' style={{}}>
              <div className='' style={{ marginTop: '10px', marginRight: '25px' }}>
                <ImageShow url={settings.author_signature_url} width={80} />
                <p className='mb-0 text-muted' style={{ textAlign: 'end' }}>Author Signature</p>
              </div>
            </div>
            <div className='invoice-footer'>
              <p style={{ marginBottom: '0', color: 'white' }}>Thank you for choosing {settings.site_name || 'our company'}!</p>
              <p style={{ marginBottom: '0', color: 'white' }}>This is a computer-generated invoice.</p>
            </div>
          </div>

          {/* Action Buttons */}
          {!Id && (
            <div className="no-print" style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 1000,
              display: 'flex',
              gap: '10px'
            }}>
              <button
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF}
                style={{
                  backgroundColor: isGeneratingPDF ? '#95a5a6' : '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  opacity: isGeneratingPDF ? 0.7 : 1
                }}
                title={isGeneratingPDF ? "Generating PDF..." : uploadserver ? "Generate and Upload PDF" : "Download as PDF"}
              >
                {isGeneratingPDF ? '⏳ Generating...' : uploadserver ? '☁️ Upload PDF' : '📥 Download PDF'}
              </button>
              <button
                onClick={() => window.print()}
                style={{
                  backgroundColor: '#2980b9',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
                title="Print Invoice"
              >
                🖨️ Print Invoice
              </button>
            </div>
          )}
        </>
      ) : (
        <p>Invoice not available.</p>
      )}
    </div>
  );
};

export default InvoicePrint;
